import { useEffect, useMemo, useRef, useState } from "react";
import { BarChart3, Pencil, Sparkles, TrendingUp } from "lucide-react";
import { useCentralState } from "@/context/CentralStateContext";
import { centralApi } from "@/lib/api";
import {
  pillars, measuresByPillar, measureById, scoreOf, pillarSummary, overallIndex,
  autopullValues, targetLabel, fmtValue,
} from "@/lib/pillars";
import { scoreTone, statusStyles } from "@/lib/central";
import { iso } from "@/lib/wellness";
import { PageHeader, SectionCard } from "@/components/shared";
import { FormModal, NumberField } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const IndexRing = ({ value }) => {
  const r = 46, c = 2 * Math.PI * r, off = value == null ? c : c - (value / 100) * c;
  return (
    <div className="relative h-[120px] w-[120px] shrink-0">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="rgba(23,35,31,0.08)" strokeWidth="11" />
        <circle cx="60" cy="60" r={r} fill="none" stroke="var(--green)" strokeWidth="11" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="font-serif-display text-3xl text-[var(--text)] tabular-nums" data-testid="pillars-overall-index">{value == null ? "—" : value}</div>
          <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">index</div>
        </div>
      </div>
    </div>
  );
};

const Sparkline = ({ points, color = "var(--green)" }) => {
  const vals = (points || []).map((p) => Number(p.value)).filter((n) => Number.isFinite(n));
  if (vals.length < 2) return null;
  const min = Math.min(...vals), max = Math.max(...vals), span = max - min || 1;
  const w = 64, h = 20;
  const step = w / (vals.length - 1);
  const d = vals.map((v, i) => `${i === 0 ? "M" : "L"}${(i * step).toFixed(1)},${(h - ((v - min) / span) * h).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible" aria-hidden="true">
      <path d={d} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const ScoreBadge = ({ score }) => {
  if (score == null) return <span className="inline-flex items-center rounded-full bg-[color:rgba(23,35,31,0.06)] px-2 py-0.5 text-[11px] text-[var(--text-faint)]">—</span>;
  const s = statusStyles[scoreTone(score)];
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums" style={{ color: s.color, backgroundColor: s.bg }}>{score}</span>
  );
};

export default function CentralPillars() {
  const { fin, life, wellness, loading: centralLoading } = useCentralState();
  const [store, setStore] = useState(null); // { measures: {id: {value, updatedAt, history}} }
  const [loading, setLoading] = useState(true);
  const [activePillar, setActivePillar] = useState("faith");
  const [logOpen, setLogOpen] = useState(false);
  const [draft, setDraft] = useState(null); // { id, value }
  const saveTimer = useRef(null);

  useEffect(() => {
    let mounted = true;
    centralApi.getPillars()
      .then((d) => { if (mounted) setStore(d.state && d.state.measures ? d.state : { measures: {} }); })
      .catch(() => setStore({ measures: {} }))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const auto = useMemo(() => autopullValues(fin, life, wellness), [fin, life, wellness]);

  const currentMap = useMemo(() => {
    const map = {};
    const manual = store?.measures || {};
    Object.keys(measureById).forEach((id) => {
      const mv = manual[id]?.value;
      if (mv != null && mv !== "") map[id] = mv;
      else if (auto[id]) map[id] = auto[id].value;
    });
    return map;
  }, [store, auto]);

  const sourceOf = (id) => {
    const mv = store?.measures?.[id]?.value;
    if (mv != null && mv !== "") return "manual";
    if (auto[id]) return auto[id].source;
    return null;
  };

  const persist = (next) => {
    setStore(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { centralApi.savePillars(next).catch(() => toast.error("Couldn't save measure")); }, 500);
  };

  const openLog = (m) => {
    const existing = store?.measures?.[m.id]?.value;
    const seed = existing != null && existing !== "" ? existing : (auto[m.id]?.value ?? "");
    setDraft({ id: m.id, value: seed });
    setLogOpen(true);
  };
  const saveLog = () => {
    const m = measureById[draft.id];
    const v = draft.value;
    if (v === "" || v == null || !Number.isFinite(Number(v))) return toast.error("Enter a number");
    const prev = store?.measures?.[m.id] || {};
    const history = [...(prev.history || []), { date: iso(), value: Number(v) }].slice(-30);
    const next = { ...store, measures: { ...(store?.measures || {}), [m.id]: { value: Number(v), updatedAt: new Date().toISOString(), history } } };
    persist(next);
    setLogOpen(false);
    toast.success(`${m.name} logged`);
  };

  if (loading || centralLoading || !store) {
    return <div className="space-y-4"><Skeleton className="h-40 rounded-[14px]" /><Skeleton className="h-96 rounded-[14px]" /></div>;
  }

  const overall = overallIndex(currentMap);
  const activeList = measuresByPillar(activePillar);
  const groups = [...new Set(activeList.map((m) => m.group))];

  return (
    <div>
      <PageHeader title="Pillar Performance" subtitle="Your whole-life BI — 5 pillars, 140 measures, scored against your targets." testid="pillars-header" />

      {/* Summary */}
      <SectionCard className="mb-4" testid="pillars-summary-card">
        <div className="flex flex-col lg:flex-row lg:items-center gap-5">
          <div className="flex items-center gap-4 shrink-0">
            <IndexRing value={overall} />
            <div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--text-muted)]">Whole-life index</div>
              <div className="font-serif-display text-lg text-[var(--text)] mt-0.5">Composite of scored pillars</div>
              <div className="text-xs text-[var(--text-muted)] mt-1 flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5 text-[var(--gold)]" />Auto-pulled from your centers where available</div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 flex-1">
            {pillars.map((p) => {
              const s = pillarSummary(p.id, currentMap);
              const active = p.id === activePillar;
              return (
                <button key={p.id} onClick={() => setActivePillar(p.id)} data-testid={`pillar-card-${p.id}`}
                  className={cn("text-left rounded-[12px] border px-3 py-2.5 transition-colors", active ? "border-[color:var(--border-strong)] bg-[var(--surface-2)]" : "border-[color:var(--border-subtle)] bg-white hover:border-[color:var(--border-strong)]")}>
                  <div className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.color }} />
                    <span className="text-[11px] font-medium text-[var(--text)] truncate">{p.short}</span>
                  </div>
                  <div className="mt-1.5 flex items-end justify-between">
                    <span className="font-serif-display text-2xl tabular-nums text-[var(--text)]" data-testid={`pillar-score-${p.id}`}>{s.score == null ? "—" : s.score}</span>
                    <span className="text-[10px] text-[var(--text-faint)]">{s.loggedCount}/{s.total}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </SectionCard>

      {/* Pillar tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="pillar-tabs">
        {pillars.map((p) => {
          const active = p.id === activePillar;
          const Icon = p.icon;
          return (
            <button key={p.id} onClick={() => setActivePillar(p.id)} data-testid={`pillar-tab-${p.id}`}
              className={cn("inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm transition-colors",
                active ? "text-white border-transparent" : "border-[color:var(--border-subtle)] bg-white text-[var(--text-muted)] hover:text-[var(--text)]")}
              style={active ? { backgroundColor: p.color } : {}}>
              <Icon className="h-4 w-4" />{p.name}
            </button>
          );
        })}
      </div>

      {/* Measures grouped */}
      <div className="space-y-4" data-testid="pillar-measures">
        {groups.map((g) => (
          <SectionCard key={g} title={g} testid={`group-${g.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            <div className="space-y-2">
              {activeList.filter((m) => m.group === g).map((m) => {
                const val = currentMap[m.id];
                const src = sourceOf(m.id);
                const score = scoreOf(m, val);
                const history = store?.measures?.[m.id]?.history;
                const p = pillars.find((x) => x.id === m.pillar);
                return (
                  <div key={m.id} className="flex items-center gap-3 rounded-[10px] border border-[color:var(--border-subtle)] bg-white px-3 py-2.5" data-testid={`measure-row-${m.id}`}>
                    <div className="min-w-0 flex-[2]">
                      <div className="text-sm text-[var(--text)] flex items-center gap-2">
                        <span className="truncate">{m.name}</span>
                        <span className="text-[10px] uppercase tracking-wide text-[var(--text-faint)] shrink-0">{m.cadence}</span>
                      </div>
                      <div className="text-[12px] text-[var(--text-muted)] truncate">{m.desc}</div>
                    </div>
                    <div className="hidden sm:flex items-center justify-center flex-1">
                      {history && history.length > 1 ? <Sparkline points={history} color={p?.color} /> : <span className="text-[11px] text-[var(--text-faint)]">no trend yet</span>}
                    </div>
                    <div className="w-28 text-right">
                      {val != null && val !== "" ? (
                        <div className="text-sm text-[var(--text)] tabular-nums" data-testid={`measure-value-${m.id}`}>{fmtValue(m, val)}</div>
                      ) : (
                        <div className="text-[12px] text-[var(--text-faint)]" data-testid={`measure-value-${m.id}`}>Not yet logged</div>
                      )}
                      {src && src !== "manual" && <div className="text-[10px] text-[var(--gold)] mt-0.5">from {src}</div>}
                    </div>
                    <div className="w-20 text-right text-[12px] text-[var(--text-muted)] tabular-nums hidden md:block">{targetLabel(m)}</div>
                    <div className="w-12 flex justify-center"><ScoreBadge score={score} /></div>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => openLog(m)} data-testid={`measure-log-${m.id}`} aria-label={`Log ${m.name}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        ))}
      </div>

      {draft && (
        <FormModal open={logOpen} onOpenChange={setLogOpen} title={`Log · ${measureById[draft.id]?.name}`}
          description={measureById[draft.id]?.desc} onSubmit={saveLog} submitLabel="Save value" testid="measure-log-modal">
          <NumberField label={`Value (${measureById[draft.id]?.unit || ""})`} value={draft.value} onChange={(e) => setDraft({ ...draft, value: e.target.value })} full testid="measure-log-input" autoFocus />
          <div className="sm:col-span-2 text-[12px] text-[var(--text-muted)]">
            Target: <span className="text-[var(--text)]">{targetLabel(measureById[draft.id])}</span>
            {auto[draft.id] && <span className="ml-2 text-[var(--gold)]">· auto value from {auto[draft.id].source}: {auto[draft.id].value}</span>}
          </div>
        </FormModal>
      )}
    </div>
  );
}
